import { useId, useState } from "react";
import { useActivePolicy } from "../../../hooks/useActivePolicy";
import { useRovingRadio } from "../../../hooks/useRovingRadio";
import { clampSodiumTarget } from "../../../lib/fuelClamps";
import { GUT_QUESTION } from "../../../lib/questionCopy";
import type { AboutYouFuel } from "./useAboutYouFuel";

/**
 * "Your fueling experience" (wizard step 3, D-16) — gut-practice choice,
 * optional custom carb/sodium targets, and the gut-training ramp. Split from
 * the old `FuelStep` (cadence moved to its own Box-cadence step); shared by the
 * wizard (race + rolling on-ramp) and the My Fuel `ProfileGateSheet`.
 *
 * Two variants:
 *  - `"wizard"` — the full step, incl. the race-day carb target field and the
 *    plan-relative ramp start hint (`rampStartHint`).
 *  - `"gate"` — the My Fuel `ProfileGateSheet` / rolling on-ramp. Hides the
 *    race-day carb field (no race day) and the ramp start hint.
 *
 * Renders the inner `.fgp-wz-fuel` block only — the caller owns the chrome.
 */
export function FuelExperienceStep({
  fuel,
  variant = "wizard",
  rampStartHint
}: {
  fuel: AboutYouFuel;
  variant?: "wizard" | "gate";
  /** Wizard-only ramp note, e.g. "Gut training starts now — ahead of your
   *  plan's first week." Appended after the standing ramp copy. */
  rampStartHint?: string;
}) {
  const showRaceDayTarget = variant === "wizard";
  // W-07 — the ramp card reads its weeks/start-percent from the active policy
  // (workoutRules.ramp) instead of hardcoded presentation constants; the
  // 10-week / 46% pair is only the not-yet-loaded fallback.
  const policy = useActivePolicy();
  const rampWeeks = policy?.workoutRules.ramp.defaultWeeks ?? 10;
  const rampStartPct = policy?.workoutRules.ramp.defaultStartPct ?? 46;
  // D11 — the ramp explainer (copy, chart, labels, footnote) is collapsed by
  // default; the header line (title + switch) carries the decision.
  const [rampInfoOpen, setRampInfoOpen] = useState(false);
  const rampBodyId = useId();
  const gutIndex = GUT_QUESTION.options.findIndex((o) => o.value === fuel.gutLevel);
  const pickGut = (value: (typeof GUT_QUESTION.options)[number]["value"]) => {
    fuel.gutTouchedRef.current = true;
    fuel.setGutLevel(value);
    // The gut answer only SUGGESTS the ramp default — and a
    // ramp that's already running never flips from here (R9).
    if (!fuel.rampAlreadyRunning) {
      fuel.rampTouchedRef.current = true;
      fuel.setRampEnabled(value === "new");
    }
    if (value === "locked") fuel.setShowTargets(true);
    else fuel.setShowTargets(false);
  };
  const gutRove = useRovingRadio({
    length: GUT_QUESTION.options.length,
    selectedIndex: gutIndex,
    onArrowSelect: (n) => pickGut(GUT_QUESTION.options[n]!.value)
  });

  return (
    <div className="fgp-wz-fuel">
      {/* ── Gut practice ─────────────────────────────────────────────── */}
      <div className="fgp-wz-field">
        <div id="fgp-wz-gut-label" className="fgp-wz-fuel__label">
          {GUT_QUESTION.label}
        </div>
        <p className="fgp-wz-fuel__desc">{GUT_QUESTION.desc}</p>
        <div
          className="fgp-wz-choices"
          style={{ flexDirection: "column" }}
          role="radiogroup"
          aria-labelledby="fgp-wz-gut-label">
          {GUT_QUESTION.options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={fuel.gutLevel === o.value}
              tabIndex={gutRove.tabIndexFor(i)}
              onKeyDown={(e) => gutRove.onKeyDown(e, i)}
              className={`fgp-wz-choice${fuel.gutLevel === o.value ? " fgp-wz-choice--on" : ""}`}
              onClick={() => pickGut(o.value)}>
              <span className="fgp-wz-choice__t">{o.title}</span>
              <span className="fgp-wz-choice__s">{o.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Custom carb targets option card ──────────────────────────── */}
      {fuel.showCarbTargetsCard ? (
        <div
          className={`fgp-wz-optcard${fuel.targetsExpanded ? " fgp-wz-optcard--on" : " fgp-wz-optcard--off"}`}>
          <div className="fgp-wz-optcard__head">
            <div className="fgp-wz-optcard__title">
              {fuel.targetsExpanded
                ? "Use my own targets"
                : "Do you know your carb targets?"}
            </div>
            {fuel.gutLevel === "tuning" ? (
              <button
                type="button"
                role="switch"
                aria-checked={fuel.targetsExpanded}
                aria-label="Custom carb targets"
                className={`fgp-wz-switch${fuel.targetsExpanded ? " fgp-wz-switch--on" : ""}`}
                onClick={() => fuel.setShowTargets((v) => !v)}>
                <span />
              </button>
            ) : null}
          </div>
          {fuel.targetsExpanded ? (
            <div className="fgp-wz-optcard__body">
              <div className="fgp-wz-targets__row">
                {showRaceDayTarget && (
                  <TargetField
                    label="Race day (g/hr)"
                    ariaLabel="Race day carbs per hour"
                    value={fuel.raceDayCarbs}
                    onChange={(n) => {
                      fuel.raceTargetTouchedRef.current = true;
                      fuel.setRaceDayCarbs(n);
                    }}
                  />
                )}
                <TargetField
                  label="Runs (g/hr)"
                  ariaLabel="Runs carbs per hour"
                  value={fuel.runCarbs}
                  onChange={(n) => {
                    fuel.perSportTouchedRef.current = true;
                    fuel.setRunCarbs(n);
                  }}
                />
                <TargetField
                  label="Rides (g/hr)"
                  ariaLabel="Rides carbs per hour"
                  value={fuel.rideCarbs}
                  onChange={(n) => {
                    fuel.perSportTouchedRef.current = true;
                    fuel.setRideCarbs(n);
                  }}
                />
                {/* W-17 — locked-in athletes can state an exact sodium mg/hr;
                    it overrides the sweat-derived estimate (0 = unset). Clamped
                    on blur so the DISPLAYED value matches what persists. */}
                <TargetField
                  label="Sodium (mg/hr)"
                  ariaLabel="Sodium milligrams per hour"
                  max={2000}
                  step={50}
                  value={fuel.sodiumMg}
                  clampOnBlur={clampSodiumTarget}
                  onChange={(n) => {
                    fuel.sodiumTouchedRef.current = true;
                    fuel.setSodiumMg(n);
                  }}
                />
              </div>
              <p className="fgp-wz-optcard__foot">
                Each is your top-end target, not a hard cap — easier or shorter days scale
                down from it. Leave any at 0 to let us pick from your plan
                {fuel.rampEnabled ? "; the gut ramp still applies on top" : ""}.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ── Gut ramp option card ─────────────────────────────────────── */}
      {fuel.rampAlreadyRunning ? (
        <div className="fgp-wz-optcard fgp-wz-optcard--on">
          <div className="fgp-wz-optcard__head">
            <div className="fgp-wz-optcard__title">Your gut ramp is already running</div>
            <button
              type="button"
              className="fgp-wz-ramplink"
              style={{ alignSelf: "center", flexShrink: 0 }}
              onClick={fuel.onDisableRamp}>
              Disable
            </button>
          </div>
          <div className="fgp-wz-optcard__body">
            <p className="fgp-wz-optcard__copy">
              {fuel.profileView?.ramp && fuel.profileView.ramp.state === "active" ? (
                <>
                  You&apos;re on <strong>gut-training week {fuel.profileView.ramp.weekNow} of{" "}
                  {fuel.profileView.ramp.weeksTotal}</strong> — this plan will fuel accordingly.
                </>
              ) : fuel.profileView?.ramp?.state === "full" ? (
                <>You&apos;re at <strong>full fuel</strong> — this plan fuels at your full targets.</>
              ) : (
                <>Your ramp is set up and this plan will fuel accordingly.</>
              )}{" "}
              Change it anytime in Preferences → Fueling.
            </p>
          </div>
        </div>
      ) : (
        <div
          className={`fgp-wz-optcard${fuel.rampEnabled ? " fgp-wz-optcard--on" : " fgp-wz-optcard--off"}`}>
          <div className="fgp-wz-optcard__head">
            <div className="fgp-wz-optcard__title">
              Start gradually — build up over {rampWeeks} weeks
              {fuel.gutLevel === "new" ? (
                <span className="fgp-wz-optcard__rec"> · recommended</span>
              ) : null}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={fuel.rampEnabled}
              aria-label="Gut ramp"
              className={`fgp-wz-switch${fuel.rampEnabled ? " fgp-wz-switch--on" : ""}`}
              onClick={() => {
                fuel.rampTouchedRef.current = true;
                fuel.setRampEnabled((v) => !v);
              }}>
              <span />
            </button>
          </div>
          {fuel.rampEnabled ? (
            <button
              type="button"
              className="fgp-wz-ramplink"
              aria-expanded={rampInfoOpen}
              aria-controls={rampBodyId}
              onClick={() => setRampInfoOpen((v) => !v)}>
              {rampInfoOpen ? "Hide" : "See how it works"}
            </button>
          ) : null}
          {fuel.rampEnabled && rampInfoOpen ? (
            <div id={rampBodyId} className="fgp-wz-optcard__body">
              <p className="fgp-wz-optcard__copy">
                We start you on a reduced carb target and increase week by week, to help your gut get used to fueling.
              </p>
              <div className="fgp-wz-rampchart" aria-hidden>
                {rampBars(rampWeeks, rampStartPct).map((b, i) => (
                  <span key={i} style={{ height: b.h, background: b.c }} />
                ))}
              </div>
              <div className="fgp-wz-rampchart__labels">
                <span>Wk 1 · {rampStartPct}%</span>
                <span>Wk {rampWeeks} · 100%</span>
              </div>
              <p className="fgp-wz-optcard__foot">
                You can jump ahead anytime — we&apos;ll keep up. This build-up continues
                across future training — your current boxes cover the first weeks of it.
                {rampStartHint ? ` ${rampStartHint}` : ""}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * W-07 — the ramp bar chart, generated from the policy's weeks + start percent
 * (was a hardcoded 10-bar/46% constant). Heights interpolate startPct → 100%
 * against the same 44px cap; colors walk the existing purple gradient buckets.
 */
function rampBars(weeks: number, startPct: number): Array<{ h: number; c: string }> {
  const n = Math.max(2, Math.round(weeks));
  const bars: Array<{ h: number; c: string }> = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const pct = startPct + (100 - startPct) * t;
    bars.push({
      h: Math.max(4, Math.round((pct / 100) * 44)),
      c: t < 0.3 ? "#D8C7F2" : t < 0.6 ? "#B5A3D1" : t < 0.9 ? "#9747FF" : "#9000DC"
    });
  }
  return bars;
}

function TargetField({
  label,
  ariaLabel,
  value,
  onChange,
  max = 140,
  step = 5,
  clampOnBlur
}: {
  label: string;
  ariaLabel: string;
  value: number;
  onChange: (n: number) => void;
  max?: number;
  step?: number;
  /** Commit-time clamp: on blur the field writes the clamped value back
   *  through `onChange`, so what the athlete SEES matches what persists. */
  clampOnBlur?: (n: number) => number;
}) {
  return (
    <label className="fgp-wz-target">
      <span className="fgp-wz-target__label">{label}</span>
      <input
        type="number"
        min={0}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onBlur={
          clampOnBlur
            ? () => {
                const clamped = clampOnBlur(value);
                if (clamped !== value) onChange(clamped);
              }
            : undefined
        }
        aria-label={ariaLabel}
      />
    </label>
  );
}
